/** Dashboard icon-grid nav — shared by DashboardPage and tests. */

export type DashboardNavItemDef = {
  /**
   * Stable key a saved Home layout stores (z8nrz7ep6x). Never derive it from
   * the label or route: Release routes to `/`, and Pending Inventory and Storage
   * share `/inventory`. Renaming an id silently drops the tile from every saved
   * layout, so add a new one instead.
   */
  id: string
  label: string
  to: string
  /** Icon background. Per tile, not per column, so a tile keeps its colour in a custom layout. */
  tile: string
  /**
   * The route behind the tile is global (no tenant guard), so only a superadmin
   * may see it — a tenant role holding the matching key would get a screen whose
   * every request 403s. See `usePermission`.
   */
  superAdminOnly?: boolean
}

export type DashboardNavGroupDef = {
  title: string
  text: string
  items: DashboardNavItemDef[]
}

/**
 * Three columns of eight, and Manage one longer since Action Items
 * (z8nrz7fkfh) joined CAPA there.
 *
 * The columns are flex siblings, so the grid is only even when they hold the
 * same number of cards. Each new tile went to a different column for that
 * reason: Pending Inventory beside Inventory, Storage in Manage, Users beside
 * Configuration.
 */
export const dashboardNavGroups: DashboardNavGroupDef[] = [
  {
    title: 'CGT Process',
    text: 'text-blue-700',
    items: [
      { id: 'transplant', label: 'Transplant', to: '/procedures', tile: 'bg-violet-600' },
      // Clean is the Cleaning page: rooms on the equipment module (z8nrz7ewt3).
      { id: 'clean', label: 'Cleaning', to: '/cleaning', tile: 'bg-blue-900' },
      { id: 'kit', label: 'Kit', to: '/kits', tile: 'bg-sky-700' },
      { id: 'mes', label: 'MES', to: '/mes', tile: 'bg-indigo-950' },
      { id: 'collection', label: 'Collection', to: '/forms', tile: 'bg-violet-800' },
      { id: 'processing', label: 'Processing', to: '/submissions', tile: 'bg-amber-500' },
      { id: 'inventory', label: 'Inventory', to: '/inventory', tile: 'bg-emerald-600' },
      {
        id: 'pending-inventory',
        label: 'Pending Inventory',
        to: '/inventory?tab=pending',
        tile: 'bg-blue-500',
      },
    ],
  },
  {
    title: 'Manage',
    text: 'text-green-700',
    items: [
      {
        id: 'upcoming-procedure',
        label: 'Upcoming Procedure',
        to: '/procedures/calendar',
        tile: 'bg-amber-700',
      },
      { id: 'capa', label: 'CAPA', to: '/capa', tile: 'bg-rose-600' },
      // Follow-ups people raise on a form or any record (z8nrz7fkfh).
      { id: 'action-items', label: 'Action Items', to: '/action-items', tile: 'bg-sky-400' },
      { id: 'notifications', label: 'Notifications', to: '/notifications', tile: 'bg-red-600' },
      { id: 'resource', label: 'Resource', to: '/resources', tile: 'bg-cyan-500' },
      { id: 'release', label: 'Release', to: '/', tile: 'bg-red-500' },
      { id: 'reports', label: 'Reports', to: '/reports', tile: 'bg-pink-700' },
      { id: 'dashboards', label: 'Dashboards', to: '/dashboards', tile: 'bg-pink-500' },
      { id: 'storage', label: 'Storage', to: '/inventory?tab=storage', tile: 'bg-blue-700' },
    ],
  },
  {
    title: 'Data Center',
    text: 'text-blue-700',
    items: [
      { id: 'master-data', label: 'Master Data', to: '/form-definitions', tile: 'bg-sky-600' },
      { id: 'form-builder', label: 'Form Builder', to: '/form-builder', tile: 'bg-sky-500' },
      { id: 'configuration', label: 'Configuration', to: '/configure', tile: 'bg-cyan-600' },
      {
        id: 'users',
        label: 'Users',
        to: '/configure?tab=users',
        tile: 'bg-lime-700',
        superAdminOnly: true,
      },
      { id: 'integration', label: 'Integration', to: '/integrations', tile: 'bg-neutral-700' },
      { id: 'patients', label: 'Patients', to: '/patients', tile: 'bg-purple-400' },
      { id: 'products', label: 'Products', to: '/products', tile: 'bg-purple-500' },
      { id: 'equipment', label: 'Equipment', to: '/equipment', tile: 'bg-green-700' },
    ],
  },
]

export type DashboardNavCard = {
  id: string
  label: string
  href: string
  group: string
  superAdminOnly: boolean
}

export function flattenDashboardNavCards(): DashboardNavCard[] {
  return dashboardNavGroups.flatMap((group) =>
    group.items.map((item) => ({
      id: item.id,
      label: item.label,
      href: item.to,
      group: group.title,
      superAdminOnly: item.superAdminOnly === true,
    })),
  )
}
